import './crop-image-panel.less';
import { useLatest } from 'ahooks';
import classNames from 'classnames';
import { cloneDeep } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { Button, Scrollbars, SliderInput } from '@meitu/saas-ui';
import { BackTitle } from '@meitu/saas-ui/es/title';
import { getTrackFunc } from '@meitu/saas-editor-core/es/framework/history/track-func';
import { Editor } from '@meitu/saas-editor-core/es/index';
import { ImageLayer } from '@meitu/saas-editor-core/es/layers/image-layer';
import { CropImgPlugin } from '@meitu/saas-editor-core/es/plugins/crop-img';
import {
    adjustImageLayerRatio,
    isLayerSizeRadio,
    rotateImage,
} from '@meitu/saas-editor-core/es/plugins/crop-img/new-components/utils';
import { RealEdit } from '@meitu/saas-editor-core/es/plugins/real-edit';
import { IImageEffectProps, IImageLayerProps, ILayerProps } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import LabelAction from '@/components/label-action';
import { getLocaleStore, useLocaleStore } from '@/i18n/index';

const ExpandRadioOptions = () => {
    const { $t } = getLocaleStore();

    return [
        {
            label: $t((v) => v.collapse.adjustCollapse.freeCutting),
            value: '0',
            icon: 'CropBold',
        },
        {
            label: '1:1',
            value: '1:1',
            icon: 'Ratio11Bold',
        },
        {
            label: '3:2',
            value: '3:2',
            icon: 'Ratio32Bold',
        },
        {
            label: '2:3',
            value: '2:3',
            icon: 'Ratio23Bold',
        },
        {
            label: '4:3',
            value: '4:3',
            icon: 'Ratio43Bold',
        },
        {
            label: '3:4',
            value: '3:4',
            icon: 'Ratio34Bold',
        },
        {
            label: '16:9',
            value: '16:9',
            icon: 'Ratio169Bold',
        },
        {
            label: '9:16',
            value: '9:16',
            icon: 'Ratio916Bold',
        },
    ];
};

const formatRotate = (rotate: number) => {
    return rotate > 180 ? rotate - 360 : rotate;
};

const CropImgPanel: React.FC<{
    setPanel: Function;
    editor: Editor;
    setIsDisableAppAndShowPanel: (isDisableAppAndShowPanel: boolean) => void;
}> = ({ setPanel, editor, setIsDisableAppAndShowPanel }) => {
    const { $t } = useLocaleStore();
    const [ratio, setRatio] = useState('0');
    const [activeLayer, setActiveLayer] = useState<ILayerProps | null>(editor.active.getPick(true));
    const activeLayerRef = useLatest(activeLayer);
    const originLayer = useRef<ILayerProps | null>(cloneDeep(editor.active.getPick(true)));

    useEffect(() => {
        const eventId = editor.active.onChange((layer, pickLayer) => {
            if (pickLayer) {
                setActiveLayer({ ...pickLayer });
            } else if (layer) {
                setActiveLayer({ ...layer });
            } else {
                setActiveLayer(null);
            }
        });

        const escHandle = editor.onEsc(() => {
            cancelCropImage();
        });

        return () => {
            editor.off(escHandle);
            editor.off(eventId);
        };
    }, []);

    useEffect(() => {
        setIsDisableAppAndShowPanel(true);
        if (activeLayer) {
            const isRadio = isLayerSizeRadio(activeLayer.width, activeLayer.height);
            if (isRadio) {
                setRatio(isRadio);
                editor.getInstance(CropImgPlugin).setIsAutoCutoutLayer(false);
            } else {
                editor.getInstance(CropImgPlugin).setIsAutoCutoutLayer(true);
            }
        }

        return () => {
            editor.getInstance(CropImgPlugin).setIsAutoCutoutLayer(true);
            setIsDisableAppAndShowPanel(false);
        };
    }, []);

    useEffect(() => {
        const handler = editor.onMouseDown((e) => {
            if (!editor.getInstance(CropImgPlugin).checkIsOperateTarget(e.target)) {
                if (JSON.stringify(originLayer.current) !== JSON.stringify(activeLayerRef.current)) {
                    confirmCropImage();
                } else {
                    cancelCropImage();
                }
            }
        }, 1000);
        return () => {
            editor.off(handler);
            editor.getInstance(CropImgPlugin).close();
        };
    }, []);

    const cancelCropImage = () => {
        if (originLayer.current) {
            originLayer.current.parentId = editor.stage.getLayer(originLayer.current.id)?.parentId;
            editor.getInstance(ImageLayer).updateLayer(originLayer.current);
            editor.getInstance(RealEdit).sendLayer(originLayer.current);
        }
        editor.getInstance(CropImgPlugin).close();
        setPanel(null);
    };

    const confirmCropImage = async () => {
        const newLayer = cloneDeep(activeLayerRef.current);
        if (newLayer && originLayer.current) {
            const screenshotUrl = await editor.getInstance(ImageLayer).runEffectAndGetUrl(newLayer);
            if (screenshotUrl && (newLayer.data as IImageLayerProps).effect) {
                ((newLayer.data as IImageLayerProps).effect as IImageEffectProps).screenshotUrl = screenshotUrl;
            }
            const parentId = editor.stage.getLayer(originLayer.current.id)?.parentId;
            newLayer.__isCropImage = false;
            newLayer.parentId = parentId;

            originLayer.current.__isCropImage = false;
            originLayer.current.parentId = parentId;

            editor.getInstance(CropImgPlugin).close();
            editor
                .getInstance(ImageLayer)
                .updateLayer(newLayer, originLayer.current, true, getTrackFunc(editor.id, originLayer.current, 'crop'));
            editor.getInstance(RealEdit).sendLayer(newLayer);
        }
        setPanel(null);
    };

    return (
        <div className="c-crop-image-panel">
            <Scrollbars style={{ width: '100%', height: '100%' }}>
                <div className="title">
                    <BackTitle
                        text={$t((v) => v.panel.imagePanel.imageCrop)}
                        handleBackClick={() => {
                            confirmCropImage();
                            setPanel(null);
                        }}
                    />
                </div>

                <div className="ratio-list">
                    {ExpandRadioOptions().map((option) => {
                        return (
                            <div
                                key={option.value}
                                onClick={() => {
                                    setRatio(option.value);
                                    // 切换的时候中心点不变，当前的长边为下一个比例的长边，如果超出的就缩放
                                    if (option.value === '0' || !activeLayer) {
                                        editor.getInstance(CropImgPlugin).setIsAutoCutoutLayer(true);
                                        return;
                                    }
                                    editor.getInstance(CropImgPlugin).setIsAutoCutoutLayer(false);
                                    const newLayer = adjustImageLayerRatio(activeLayer, option.value);

                                    editor.getInstance(ImageLayer).updateLayer(newLayer);
                                }}
                                className={classNames({
                                    item: true,
                                    active: option.value === ratio,
                                })}
                            >
                                <IconFont name={option.icon} fontSize={20} />
                                {option.label}
                            </div>
                        );
                    })}
                </div>

                <div className="rotate-wrap">
                    <LabelAction title={$t((v) => v.panel.markupPanel.rotate)}>
                        <div className="input-wrap">
                            <SliderInput
                                value={formatRotate(
                                    parseInt(`${(activeLayer?.data as IImageLayerProps)?.imageTransform.rotate || 0}`),
                                )}
                                showStepBtn={true}
                                max={180}
                                min={-180}
                                type="horizontal"
                                onChange={(val: number) => {
                                    if (activeLayer) {
                                        const newLayer = rotateImage(activeLayer, val);
                                        editor.getInstance(ImageLayer).updateLayer(newLayer);
                                    }
                                }}
                            />
                        </div>
                    </LabelAction>
                </div>
                <div className="operate">
                    <Button
                        type="ghost"
                        onClick={() => {
                            cancelCropImage();
                        }}
                    >
                        {$t((v) => v.imageEffectPanel.imageBlur.cancel)}
                    </Button>
                    <Button
                        className="done"
                        onClick={() => {
                            confirmCropImage();
                        }}
                    >
                        {$t((v) => v.imageEffectPanel.imageBlur.apply)}
                    </Button>
                </div>
            </Scrollbars>
        </div>
    );
};

export default CropImgPanel;
